"use no memo";

import { Spinner } from "@choose/ui/spinner";
import type { Row } from "@tanstack/react-table";
import type { VirtualItem } from "@tanstack/react-virtual";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { features } from "~/components/common/tickets/ticket-list";
import { TicketListItem } from "~/components/common/tickets/ticket-list-item";
import {
  DataListVirtualSpacer,
  DataListVirtualStatusRow,
} from "~/components/ui/data-list/data-list-virtual";
import type { Ticket } from "~/stores/tickets-store";

export function TicketListVirtual({
  estimateSize = 44,
  hasMore,
  isFetchingMore = false,
  onFetchMore,
  overscan = 10,
  rows,
}: {
  estimateSize?: number;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onFetchMore?: () => void;
  overscan?: number;
  rows: Row<typeof features, Ticket>[];
}) {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  const isInfiniteMode = typeof onFetchMore === "function";

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => estimateSize,
    getScrollElement: () => parentRef.current,
    overscan: overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Latch onFetchMore per row count: virtualItems gets a new identity every
  // scroll frame, so without it the effect fires duplicate page requests
  // before the consumer flips isFetchingMore, and loops at end-of-data when
  // hasMore is never set.
  const fetchMoreFiredAtCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isInfiniteMode || hasMore === false || isFetchingMore) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (fetchMoreFiredAtCountRef.current === rows.length) return;

    if (lastItem.index >= rows.length - 1) {
      fetchMoreFiredAtCountRef.current = rows.length;
      onFetchMore();
    }
  }, [
    hasMore,
    isFetchingMore,
    isInfiniteMode,
    onFetchMore,
    rows.length,
    virtualItems,
  ]);

  return (
    <div
      ref={parentRef}
      className="col-[1/_-1] grid min-h-0 w-full grid-cols-subgrid overflow-x-hidden overflow-y-auto"
    >
      <div
        className="col-[1/_-1] grid min-h-0 w-full grid-cols-subgrid items-start"
        style={{
          height: `${totalSize}px`,
        }}
      >
        <TicketListVirtualBody
          isFetchingMore={isFetchingMore}
          isInfiniteMode={!!hasMore}
          loadingMoreMessage={t("data_list.loading")}
          measureRowRef={virtualizer.measureElement}
          rows={rows}
          totalSize={totalSize}
          virtualItems={virtualItems}
        />
      </div>
    </div>
  );
}

function TicketListVirtualBody({
  isFetchingMore,
  isInfiniteMode,
  loadingMoreMessage,
  measureRowRef,
  rows,
  totalSize,
  virtualItems,
}: {
  isFetchingMore: boolean;
  isInfiniteMode: boolean;
  loadingMoreMessage: ReactNode;
  measureRowRef?: (element: HTMLDivElement | null) => void;
  rows: Row<typeof features, Ticket>[];
  totalSize: number;
  virtualItems: VirtualItem[];
}) {
  const showFetchingRow = isInfiniteMode && isFetchingMore;

  const leadingSpacerHeight =
    virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const trailingSpacerHeight =
    virtualItems.length > 0
      ? Math.max(
          0,
          totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0),
        )
      : 0;

  const renderedRows: ReactNode[] = [];

  if (leadingSpacerHeight > 0) {
    renderedRows.push(
      <DataListVirtualSpacer
        key="virtual-spacer-start"
        height={leadingSpacerHeight}
      />,
    );
  }

  virtualItems.forEach((virtualRow) => {
    const row = rows[virtualRow.index];

    if (!row) return;

    renderedRows.push(
      <TicketListItem
        key={row.id}
        row={row}
        ref={measureRowRef as unknown as React.Ref<HTMLAnchorElement>}
        data-index={virtualRow.index}
        data-row-id={row.id}
      />,
    );
  });

  if (trailingSpacerHeight > 0) {
    renderedRows.push(
      <DataListVirtualSpacer
        key="virtual-spacer-end"
        height={trailingSpacerHeight}
      />,
    );
  }

  if (showFetchingRow) {
    renderedRows.push(
      <DataListVirtualStatusRow
        key="virtual-status-loading"
        className="col-span-full"
      >
        <div className="flex items-center justify-center gap-2">
          <Spinner className="size-4 opacity-60" />
          {loadingMoreMessage}
        </div>
      </DataListVirtualStatusRow>,
    );
  }

  return <>{renderedRows}</>;
}
